import { api } from './client';
import type {
  Account,
  Shop,
  User,
  Customer,
  CustomerRemark,
  Inventory,
  InventoryImage,
  InventoryTransit,
  Invoice,
  PosSession,
  Cashflow,
  Expense,
  Supplier,
  Filing,
  VaultEntry,
  Rental,
  TradeIn,
  Job,
  PackageRedemption,
  Appointment,
  ApiResponse,
  LoginRequest,
  LoginResponse,
} from '@motra/shared';

// ─── Auth ────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (data: LoginRequest) =>
    api.post<ApiResponse<LoginResponse>>('/auth/login', data),

  logout: () => api.post<ApiResponse>('/auth/logout'),

  verify: () => api.get<ApiResponse<{ valid: boolean }>>('/auth/verify'),

  initialize: (shopId: string) =>
    api.get<ApiResponse<{ account: Account; shop: Shop }>>(`/auth/initialize?shopid=${shopId}`),

  initializeManager: (accountId: string) =>
    api.get<ApiResponse<{ account: Account }>>(`/auth/initialize?account=${accountId}`),
};

// ─── Customers ───────────────────────────────────────────────────────────────

export const customersApi = {
  list: (shopId: string, params?: { keyword?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<Customer[]>>(`/customers?${q}`);
  },

  upsert: (data: Partial<Customer> & { shopid?: string }) =>
    api.post<ApiResponse<Customer>>('/customers', data),

  delete: (customerId: string) =>
    api.delete<ApiResponse>(`/customers/${customerId}`),

  getRemarks: (customerId: string) =>
    api.get<ApiResponse<CustomerRemark[]>>(`/customers/${customerId}/remarks`),

  addRemark: (customerId: string, remark: string) =>
    api.post<ApiResponse<CustomerRemark>>(`/customers/${customerId}/remarks`, { remark }),

  getHistory: (customerId: string) =>
    api.get<ApiResponse<Invoice[]>>(`/customers/${customerId}/history`),

  getCredits: (customerId: string) =>
    api.get<ApiResponse<{ credits: number; transactions: unknown[] }>>(`/customers/${customerId}/credits`),

  redeem: (customerId: string, data: { pin: string; amount: number; invoice_id?: string }) =>
    api.post<ApiResponse>(`/customers/${customerId}/redeem`, data),
};

// ─── Inventory ───────────────────────────────────────────────────────────────

export const inventoryApi = {
  list: (shopId: string, params?: { category?: string; keyword?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<Inventory[]>>(`/inventory?${q}`);
  },

  listAll: (accountId: string) =>
    api.get<ApiResponse<Inventory[]>>(`/inventory/all?accountid=${accountId}`),

  depleted: (shopId: string) =>
    api.get<ApiResponse<Inventory[]>>(`/inventory/depleted?shopid=${shopId}`),

  upsert: (data: Partial<Inventory> & { shopid?: string }) =>
    api.post<ApiResponse<Inventory>>('/inventory', data),

  delete: (inventoryId: string) =>
    api.delete<ApiResponse>(`/inventory/${inventoryId}`),

  purge: (shopId: string) =>
    api.delete<ApiResponse>(`/inventory/purge?shopid=${shopId}`),

  batch: (shopId: string, items: Partial<Inventory>[]) =>
    api.post<ApiResponse<{ created: number; updated: number }>>('/inventory/batch', { shopid: shopId, items }),

  transfer: (data: { inventory_id: string; source_shop_id: string; dest_shop_id: string; units: number }) =>
    api.post<ApiResponse<InventoryTransit>>('/inventory/transfer', data),

  transit: (shopId: string) =>
    api.get<ApiResponse<InventoryTransit[]>>(`/inventory/transit?shopid=${shopId}`),

  completeTransit: (transitId: string, action: 'approve' | 'reject' | 'cancel') =>
    api.post<ApiResponse>(`/inventory/transit/${transitId}`, { action }),

  getImages: (inventoryId: string) =>
    api.get<ApiResponse<InventoryImage[]>>(`/inventory/${inventoryId}/images`),
};

// ─── Invoices ────────────────────────────────────────────────────────────────

export const invoicesApi = {
  generate: (data: Partial<Invoice> & { shopid: string }) =>
    api.post<ApiResponse<Invoice>>('/invoices', data),

  retrieve: (invoiceId: string) =>
    api.get<ApiResponse<Invoice>>(`/invoices/${invoiceId}`),

  void: (invoiceId: string, reason?: string) =>
    api.post<ApiResponse>(`/invoices/${invoiceId}/void`, { reason }),

  list: (shopId: string, params?: { session_id?: string; status?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<Invoice[]>>(`/invoices?${q}`);
  },
};

// ─── Payments ────────────────────────────────────────────────────────────────

export const paymentsApi = {
  stripeCharge: (data: { amount: number; payment_method_id: string; invoice_id?: string; shopid: string }) =>
    api.post<ApiResponse<{ charge_id: string }>>('/payments/stripe/charge', data),

  stripeTopup: (data: { amount: number; customer_id: string; shopid: string }) =>
    api.post<ApiResponse<{ charge_id: string }>>('/payments/stripe/topup', data),

  stripeRefund: (data: { charge_id: string; amount?: number }) =>
    api.post<ApiResponse<{ refund_id: string }>>('/payments/stripe/refund', data),

  qrCreate: (data: { amount: number; shopid: string; invoice_id?: string }) =>
    api.post<ApiResponse<{ qr_url: string; reference: string }>>('/payments/qr/create', data),

  qrCheck: (reference: string) =>
    api.get<ApiResponse<{ status: string; paid: boolean }>>(`/payments/qr/check?reference=${reference}`),

  fomoCreate: (data: { amount: number; shopid: string; invoice_id?: string }) =>
    api.post<ApiResponse<{ payment_url: string; reference: string }>>('/payments/fomo/create', data),

  fomoCheck: (reference: string) =>
    api.get<ApiResponse<{ status: string; paid: boolean }>>(`/payments/fomo/check?reference=${reference}`),

  walletPay: (data: { customer_id: string; amount: number; invoice_id: string; pin: string }) =>
    api.post<ApiResponse<{ balance: number }>>('/payments/wallet/pay', data),

  withdraw: (data: { shopid: string; amount: number; type: string; note?: string }) =>
    api.post<ApiResponse<VaultEntry>>('/payments/vault/withdraw', data),

  getVault: (shopId: string, params?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<VaultEntry[]>>(`/payments/vault?${q}`);
  },
};

// ─── Cashflow ────────────────────────────────────────────────────────────────

export const cashflowApi = {
  add: (data: Partial<Cashflow> & { shopid: string }) =>
    api.post<ApiResponse<Cashflow>>('/cashflow', data),

  addExpense: (data: Partial<Expense> & { shopid: string }) =>
    api.post<ApiResponse<Expense>>('/cashflow/expense', data),

  list: (shopId: string, sessionId?: string) => {
    const q = new URLSearchParams({ shopid: shopId, ...(sessionId ? { session_id: sessionId } : {}) });
    return api.get<ApiResponse<Cashflow[]>>(`/cashflow?${q}`);
  },

  listExpenses: (shopId: string, sessionId?: string) => {
    const q = new URLSearchParams({ shopid: shopId, ...(sessionId ? { session_id: sessionId } : {}) });
    return api.get<ApiResponse<Expense[]>>(`/cashflow/expenses?${q}`);
  },
};

// ─── Sessions ────────────────────────────────────────────────────────────────

export const sessionsApi = {
  status: (shopId: string) =>
    api.get<ApiResponse<PosSession | null>>(`/sessions/status?shopid=${shopId}`),

  toggle: (data: { shopid: string; action: 'open' | 'close'; opening_cash?: number; closing_cash?: number }) =>
    api.post<ApiResponse<PosSession>>('/sessions/toggle', data),

  opening: (sessionId: string) =>
    api.get<ApiResponse<{ opening_cash: number }>>(`/sessions/${sessionId}/opening`),

  closing: (sessionId: string) =>
    api.get<ApiResponse<{ balances: Record<string, number> }>>(`/sessions/${sessionId}/closing`),

  settle: (sessionId: string, data: { closing_cash: number; note?: string }) =>
    api.post<ApiResponse<PosSession>>(`/sessions/${sessionId}/settle`, data),

  summary: (sessionId: string) =>
    api.get<ApiResponse<{
      session: PosSession;
      total_sales: number;
      total_cash: number;
      total_invoices: number;
      payment_breakdown: Record<string, number>;
    }>>(`/sessions/${sessionId}/summary`),
};

// ─── Users ───────────────────────────────────────────────────────────────────

export const usersApi = {
  list: (accountId: string) =>
    api.get<ApiResponse<User[]>>(`/users?accountid=${accountId}`),

  upsert: (data: Partial<User>) =>
    api.post<ApiResponse<User>>('/users', data),

  delete: (userId: string) =>
    api.delete<ApiResponse>(`/users/${userId}`),

  punchcard: (userId: string, data: { action: 'in' | 'out'; shopid: string }) =>
    api.post<ApiResponse>(`/users/${userId}/punchcard`, data),

  attendance: (userId: string, params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>);
    return api.get<ApiResponse<unknown[]>>(`/users/${userId}/attendance?${q}`);
  },
};

// ─── Suppliers ───────────────────────────────────────────────────────────────

export const suppliersApi = {
  list: (accountId: string) =>
    api.get<ApiResponse<Supplier[]>>(`/suppliers?accountid=${accountId}`),

  upsert: (data: Partial<Supplier>) =>
    api.post<ApiResponse<Supplier>>('/suppliers', data),

  delete: (supplierId: string) =>
    api.delete<ApiResponse>(`/suppliers/${supplierId}`),
};

// ─── Stores ──────────────────────────────────────────────────────────────────

export const storesApi = {
  create: (data: Partial<Shop> & { account_id: string }) =>
    api.post<ApiResponse<Shop>>('/stores', data),

  get: (shopId: string) =>
    api.get<ApiResponse<Shop>>(`/stores/${shopId}`),

  update: (shopId: string, data: Partial<Shop>) =>
    api.put<ApiResponse<Shop>>(`/stores/${shopId}`, data),

  delete: (shopId: string) =>
    api.delete<ApiResponse>(`/stores/${shopId}`),

  sync: (shopId: string) =>
    api.post<ApiResponse>(`/stores/${shopId}/sync`),

  settings: (shopId: string) =>
    api.get<ApiResponse<Shop>>(`/stores/${shopId}/settings`),

  updateGeneral: (accountId: string, data: Partial<Account>) =>
    api.patch<ApiResponse<Account>>(`/stores/general?accountid=${accountId}`, data),
};

// ─── Reports ─────────────────────────────────────────────────────────────────

export const reportsApi = {
  dashboard: (accountId: string) =>
    api.get<ApiResponse<{
      monthly_sales: unknown[];
      staff_performance: unknown[];
      payment_distribution: unknown[];
    }>>(`/reports/dashboard?accountid=${accountId}`),

  generate: (data: {
    type: string;
    shopid?: string;
    accountid?: string;
    from: string;
    to: string;
    format?: string;
  }) => api.post<ApiResponse<unknown>>('/reports/generate', data),

  expenses: (shopId: string, params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<Expense[]>>(`/reports/expenses?${q}`);
  },

  storeSummary: (shopId: string, params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<unknown>>(`/reports/store-summary?${q}`);
  },

  custom: (data: { query_type: string; params: Record<string, unknown> }) =>
    api.post<ApiResponse<unknown>>('/reports/custom', data),
};

// ─── Tables ──────────────────────────────────────────────────────────────────

export const tablesApi = {
  read: (shopId: string) =>
    api.get<ApiResponse<unknown>>(`/tables?shopid=${shopId}`),

  update: (shopId: string, data: unknown) =>
    api.put<ApiResponse<unknown>>(`/tables?shopid=${shopId}`, data),
};

// ─── Filing ──────────────────────────────────────────────────────────────────

export const filingApi = {
  get: (accountId: string) =>
    api.get<ApiResponse<Filing[]>>(`/filing?accountid=${accountId}`),

  update: (filingId: string, data: Partial<Filing>) =>
    api.put<ApiResponse<Filing>>(`/filing/${filingId}`, data),
};

// ─── Modules ─────────────────────────────────────────────────────────────────

export const modulesApi = {
  update: (data: { shopid: string; modules: string[] }) =>
    api.post<ApiResponse>('/modules', data),

  delete: (moduleId: string) =>
    api.delete<ApiResponse>(`/modules/${moduleId}`),

  posConfig: (shopId: string) =>
    api.get<ApiResponse<unknown>>(`/modules/pos-config?shopid=${shopId}`),

  updatePosConfig: (shopId: string, data: unknown) =>
    api.put<ApiResponse<unknown>>(`/modules/pos-config?shopid=${shopId}`, data),

  // Rental
  listRentals: (shopId: string) =>
    api.get<ApiResponse<Rental[]>>(`/modules/rental?shopid=${shopId}`),

  createRental: (data: Partial<Rental> & { shopid: string }) =>
    api.post<ApiResponse<Rental>>('/modules/rental', data),

  updateRental: (rentalId: string, data: Partial<Rental>) =>
    api.put<ApiResponse<Rental>>(`/modules/rental/${rentalId}`, data),

  deleteRental: (rentalId: string) =>
    api.delete<ApiResponse>(`/modules/rental/${rentalId}`),

  // Trade-In
  createTradeIn: (data: Partial<TradeIn> & { shopid: string }) =>
    api.post<ApiResponse<TradeIn>>('/modules/tradein', data),

  updateTradeIn: (tradeInId: string, data: Partial<TradeIn>) =>
    api.put<ApiResponse<TradeIn>>(`/modules/tradein/${tradeInId}`, data),

  // Jobs
  listJobs: (shopId: string) =>
    api.get<ApiResponse<Job[]>>(`/modules/jobs?shopid=${shopId}`),

  createJob: (data: Partial<Job> & { shopid: string }) =>
    api.post<ApiResponse<Job>>('/modules/jobs', data),

  updateJob: (jobId: string, data: Partial<Job>) =>
    api.put<ApiResponse<Job>>(`/modules/jobs/${jobId}`, data),

  // Package
  listPackages: (customerId: string) =>
    api.get<ApiResponse<PackageRedemption[]>>(`/modules/package?customer_id=${customerId}`),

  redeemPackage: (data: { customer_id: string; package_id: string; qty: number }) =>
    api.post<ApiResponse<PackageRedemption>>('/modules/package/redeem', data),

  // Appointment
  listAppointments: (shopId: string, params?: { from?: string; to?: string }) => {
    const q = new URLSearchParams({ shopid: shopId, ...(params as Record<string, string>) });
    return api.get<ApiResponse<Appointment[]>>(`/modules/appointment?${q}`);
  },

  createAppointment: (data: Partial<Appointment> & { shopid: string }) =>
    api.post<ApiResponse<Appointment>>('/modules/appointment', data),

  updateAppointment: (appointmentId: string, data: Partial<Appointment>) =>
    api.put<ApiResponse<Appointment>>(`/modules/appointment/${appointmentId}`, data),
};

// ─── Upload ──────────────────────────────────────────────────────────────────

export const uploadApi = {
  uploadFile: (formData: FormData) =>
    api.upload<ApiResponse<{ url: string; r2_key: string }>>('/upload', formData),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export const notificationsApi = {
  email: (data: { to: string; subject: string; body: string }) =>
    api.post<ApiResponse>('/notifications/email', data),

  sms: (data: { to: string; message: string }) =>
    api.post<ApiResponse>('/notifications/sms', data),
};
